/**
 * 5-step onboarding quiz — synthesis §10.
 *   Step 1: Vibe types (multi-select 1-3)
 *   Step 2: Pal name
 *   Step 3: Three quick preferences (sliders)
 *   Step 4: Tone preview (carousel)
 *   Step 5: Privacy posture
 *
 * Posts to /api/pals/onboard via api.palOnboard, then calls onComplete with
 * the resulting profile so the parent re-renders into VibeHome.
 */

import { useState } from 'react';
import { api, type PalProfileResponse, type VeilMode } from '../cloud/api';
import {
    ALLOWED_VIBE_CLASSES,
    DEFAULT_SIGNAL_TOGGLES,
    TONE_VARIANTS,
    draftToRequest,
    emptyDraft,
    validateDraft,
    veilModeToTogglesPreset,
    type OnboardingDraft,
} from '../vibe/onboarding';

type Step = 1 | 2 | 3 | 4 | 5;

export function OnboardingWizard({ onComplete }: { onComplete: (p: PalProfileResponse) => void }) {
    const [step, setStep] = useState<Step>(1);
    const [draft, setDraft] = useState<OnboardingDraft>(emptyDraft());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const v = validateDraft(draft);

    function next() { setStep((s) => (s < 5 ? ((s + 1) as Step) : s)); }
    function back() { setStep((s) => (s > 1 ? ((s - 1) as Step) : s)); }

    async function submit() {
        if (!v.valid) return;
        setSubmitting(true);
        setError(null);
        try {
            const profile = await api.palOnboard(draftToRequest(draft));
            onComplete(profile);
        } catch (err) {
            const e = err as Error;
            setError(e.message ?? 'submit_failed');
            setSubmitting(false);
        }
    }

    return (
        <div className="card max-w-md mx-auto">
            <div className="muted text-xs mb-2">Step {step} of 5</div>

            {step === 1 && <Step1VibeClasses draft={draft} setDraft={setDraft} error={v.vibeClasses} />}
            {step === 2 && <Step2Name draft={draft} setDraft={setDraft} error={v.palName} />}
            {step === 3 && <Step3Preferences draft={draft} setDraft={setDraft} />}
            {step === 4 && <Step4Tone draft={draft} setDraft={setDraft} />}
            {step === 5 && <Step5Privacy draft={draft} setDraft={setDraft} />}

            <div className="mt-6 flex gap-2 justify-between">
                <button
                    className="btn-secondary"
                    onClick={back}
                    disabled={step === 1 || submitting}
                >Back</button>
                {step < 5 ? (
                    <button
                        className="btn-primary"
                        onClick={next}
                        disabled={(step === 1 && draft.vibe_classes.length === 0) ||
                                  (step === 2 && !!v.palName)}
                    >Next</button>
                ) : (
                    <button
                        className="btn-primary"
                        onClick={submit}
                        disabled={!v.valid || submitting}
                    >{submitting ? 'Creating…' : 'Meet your Pal'}</button>
                )}
            </div>

            {error && (
                <p className="text-danger text-sm mt-3" role="alert">
                    Couldn't create your Pal — {humanizeError(error)}.
                </p>
            )}
        </div>
    );
}

/* ───── Step 1 — Vibe classes ───────────────────────────────────── */

function Step1VibeClasses({ draft, setDraft, error }: {
    draft: OnboardingDraft;
    setDraft: (u: OnboardingDraft) => void;
    error: string | null;
}) {
    function toggle(name: string) {
        const has = draft.vibe_classes.includes(name);
        if (has) {
            setDraft({ ...draft, vibe_classes: draft.vibe_classes.filter((v) => v !== name) });
        } else {
            if (draft.vibe_classes.length >= 3) return;
            setDraft({ ...draft, vibe_classes: [...draft.vibe_classes, name] });
        }
    }

    return (
        <>
            <h2 className="text-lg font-semibold mb-1">Pick your vibe</h2>
            <p className="muted text-sm mb-4">Choose 1 to 3 — your Pal grows up around these.</p>
            <div className="grid grid-cols-2 gap-2">
                {ALLOWED_VIBE_CLASSES.map((c) => {
                    const selected = draft.vibe_classes.includes(c.name);
                    return (
                        <button
                            key={c.name}
                            type="button"
                            onClick={() => toggle(c.name)}
                            className={`p-3 rounded-lg text-left border transition ${
                                selected
                                    ? 'bg-brand/15 border-brand'
                                    : 'border-line hover:border-brand/50'
                            }`}
                        >
                            <div className="font-medium">{c.label}</div>
                            <div className="muted text-xs mt-0.5">{c.blurb}</div>
                        </button>
                    );
                })}
            </div>
            {error && <p className="muted text-xs mt-2 text-danger">{error}</p>}
        </>
    );
}

/* ───── Step 2 — Pal name ───────────────────────────────────────── */

function Step2Name({ draft, setDraft, error }: {
    draft: OnboardingDraft;
    setDraft: (u: OnboardingDraft) => void;
    error: string | null;
}) {
    return (
        <>
            <h2 className="text-lg font-semibold mb-1">Name your Pal</h2>
            <p className="muted text-sm mb-4">You can change this any time. Most people keep "Moji".</p>
            <input
                className="w-full p-3 rounded-lg border border-line"
                value={draft.pal_name}
                maxLength={31}
                onChange={(e) => setDraft({ ...draft, pal_name: e.target.value })}
                aria-invalid={!!error}
            />
            {error && <p className="text-danger text-xs mt-2">{error}</p>}
        </>
    );
}

/* ───── Step 3 — Preferences (3 sliders) ───────────────────────── */

function Step3Preferences({ draft, setDraft }: {
    draft: OnboardingDraft;
    setDraft: (u: OnboardingDraft) => void;
}) {
    return (
        <>
            <h2 className="text-lg font-semibold mb-1">Three quick reads</h2>
            <p className="muted text-sm mb-4">Slide to where you sit. Your Pal calibrates from this.</p>
            <Slider
                label="Crowds"
                left="avoid them"
                right="love them"
                value={draft.crowds}
                onChange={(v) => setDraft({ ...draft, crowds: v })}
            />
            <Slider
                label="Novelty"
                left="stick with what works"
                right="love trying new"
                value={draft.novelty}
                onChange={(v) => setDraft({ ...draft, novelty: v })}
            />
            <Slider
                label="Pace"
                left="planned ahead"
                right="spontaneous"
                value={draft.pace}
                onChange={(v) => setDraft({ ...draft, pace: v })}
            />
        </>
    );
}

function Slider({ label, left, right, value, onChange }: {
    label: string; left: string; right: string;
    value: number; onChange: (v: number) => void;
}) {
    return (
        <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
                <span className="muted">{left}</span>
                <span className="font-medium">{label}</span>
                <span className="muted">{right}</span>
            </div>
            <input
                type="range"
                min={-1} max={1} step={0.1}
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="w-full"
                aria-label={label}
            />
        </div>
    );
}

/* ───── Step 4 — Tone preview ──────────────────────────────────── */

function Step4Tone({ draft, setDraft }: {
    draft: OnboardingDraft;
    setDraft: (u: OnboardingDraft) => void;
}) {
    return (
        <>
            <h2 className="text-lg font-semibold mb-1">Voice</h2>
            <p className="muted text-sm mb-4">Pick a tone for your Pal. Easy to change later.</p>
            <div className="space-y-2">
                {TONE_VARIANTS.map((t) => {
                    const selected = draft.tone_preference === t.value;
                    return (
                        <button
                            key={t.value}
                            type="button"
                            onClick={() => setDraft({ ...draft, tone_preference: t.value })}
                            className={`w-full p-3 rounded-lg text-left border transition ${
                                selected
                                    ? 'bg-brand/15 border-brand'
                                    : 'border-line hover:border-brand/50'
                            }`}
                        >
                            <div className="flex items-baseline justify-between">
                                <div className="font-medium">{t.label}</div>
                                <div className="muted text-xs">{t.blurb}</div>
                            </div>
                            <div className="text-sm mt-1 italic">"{t.preview}"</div>
                        </button>
                    );
                })}
            </div>
        </>
    );
}

/* ───── Step 5 — Privacy ───────────────────────────────────────── */

function Step5Privacy({ draft, setDraft }: {
    draft: OnboardingDraft;
    setDraft: (u: OnboardingDraft) => void;
}) {
    function pick(mode: VeilMode) {
        setDraft({
            ...draft,
            privacy_mode: mode,
            signal_toggles: veilModeToTogglesPreset(mode),
        });
    }
    return (
        <>
            <h2 className="text-lg font-semibold mb-1">Privacy posture</h2>
            <p className="muted text-sm mb-4">
                Your Pal keeps things private by default. You can adjust per-feature in settings later.
            </p>
            <PrivacyChoice
                value="full"
                title="Full Veil"
                blurb="Nothing leaves your device. Most private. The network can't help your Pal recommend places."
                selected={draft.privacy_mode === 'full'}
                onSelect={pick}
            />
            <PrivacyChoice
                value="soft"
                title="Soft Veil — recommended"
                blurb="Anonymized place visits + qualitative tags flow to the network so your Pal can use Echoes from people like you. Music/health/contacts stay private."
                selected={draft.privacy_mode === 'soft'}
                onSelect={pick}
            />
            <PrivacyChoice
                value="open"
                title="Open"
                blurb="Everything in Soft, plus alerts (speed traps) and music vibe map. Trade-off: more visibility for richer social signal."
                selected={draft.privacy_mode === 'open'}
                onSelect={pick}
            />
        </>
    );
}

function PrivacyChoice({ value, title, blurb, selected, onSelect }: {
    value: VeilMode; title: string; blurb: string;
    selected: boolean; onSelect: (v: VeilMode) => void;
}) {
    return (
        <button
            type="button"
            onClick={() => onSelect(value)}
            className={`w-full p-3 rounded-lg text-left border transition mb-2 ${
                selected ? 'bg-success/20 border-success' : 'border-line hover:border-success/50'
            }`}
        >
            <div className="font-medium">{title}</div>
            <div className="muted text-xs mt-1">{blurb}</div>
        </button>
    );
}

/* ───── helpers ────────────────────────────────────────────────── */

function humanizeError(err: string): string {
    if (err === 'already_onboarded') return 'you already have a Pal';
    if (err.startsWith('vibe_class_unknown')) return 'one of the vibes is invalid';
    if (err === 'pal_name_length') return 'your Pal name is too short or too long';
    return err.replace(/_/g, ' ');
}

// Re-export for tests.
export { DEFAULT_SIGNAL_TOGGLES };
